import { Grid, Typography } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { hasAnyAccessToResource } from "api/general";
import { useTiers } from "api/tiers";
import { DateInput } from "components/FormControls/DateInput";
import { DateTimeInput } from "components/FormControls/DateTimeInput";
import { FormSection } from "components/FormControls/FormSection";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { TextInput } from "components/FormControls/TextInput";
import { ReactComponent as HelpCircleIcon } from "components/Icons/help-circle.svg";
import { Tooltip } from "components/Tooltips/Tooltip";
import { genderList } from "constants/general";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { useManageInfiniteScroll } from "hooks/useManageInfiniteScroll";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    tooltipIcon: {
      cursor: "pointer",
      width: "24px",
      height: "24px",
      color: theme.gray500,
    },
    tooltipTitle: {
      fontSize: "14px",
      fontWeight: 500,
      lineHeight: "24px",
      color: theme.gray800,
    },
    tooltipDesc: {
      whiteSpace: "break-spaces",
      marginTop: theme.spacing(2),
      fontSize: "12px",
      lineHeight: "20px",
      fontWeight: 400,
    },
  }),
);

interface Props {
  isEditMode: boolean;
  identificationMethod?: string;
  tier?: string;
}

export const BasicInformation = ({
  identificationMethod,
  isEditMode,
  tier,
}: Props) => {
  const { message } = useMessage();
  const { useTierData, useInfiniteTiersList } = useTiers();

  const classes = useStyles();
  const hasAnyTierPermission = hasAnyAccessToResource("LEVEL");
  const {
    data: infiniteTiersList,
    size: tiersSize,
    setSize: setTiersSize,
    isValidating,
  } = useInfiniteTiersList();

  const { infiniteData: tiersList, anyElementsToFetch: anyTiersToFetch } =
    useManageInfiniteScroll(infiniteTiersList || []);

  const { data: selectedTier } = useTierData(tier as string);

  return (
    <FormSection title={message({ id: "basicInformation" })}>
      <Grid container rowSpacing={12} columnSpacing={16}>
        <Grid item xs={6}>
          <TextInput
            label={message({ id: "firstName" })}
            name="firstName"
            data-test-id="member-first-name"
            width="100%"
            data-hj-suppress
          />
        </Grid>
        <Grid item xs={6}>
          <TextInput
            label={message({ id: "lastName" })}
            name="lastName"
            data-test-id="member-last-name"
            width="100%"
            data-hj-suppress
          />
        </Grid>
        <Grid item xs={6}>
          <TextInput
            label={
              identificationMethod === "email" ? (
                <RequiredFieldLabel text={message({ id: "email" })} />
              ) : (
                message({ id: "email" })
              )
            }
            name="email"
            data-test-id="member-email"
            width="100%"
            data-hj-suppress
          />
        </Grid>
        <Grid item xs={6}>
          <TextInput
            label={
              identificationMethod === "phone" ? (
                <RequiredFieldLabel text={message({ id: "phoneNumber" })} />
              ) : (
                message({ id: "phoneNumber" })
              )
            }
            name="phone"
            data-test-id="member-phone"
            width="100%"
            data-hj-suppress
          />
        </Grid>
        <Grid item xs={6}>
          <TextInput
            label={
              identificationMethod === "loyaltyCardNumber" ? (
                <RequiredFieldLabel
                  text={message({
                    id: "loyaltyCardNumber",
                  })}
                />
              ) : (
                message({ id: "loyaltyCardNumber" })
              )
            }
            name="loyaltyCardNumber"
            data-test-id="member-card-number"
            width="100%"
            data-hj-suppress
          />
        </Grid>
        <Grid item xs={6}>
          <TextInput
            label={message({ id: "referrerToken" })}
            name="referrerToken"
            data-test-id="member-referrer-token"
            width="100%"
            disabled={isEditMode}
            InputProps={{
              endAdornment: (
                <Tooltip
                  text={
                    <span data-hj-suppress>
                      <Typography className={classes.tooltipTitle}>
                        {message({ id: "referrerToken" })}
                      </Typography>
                      <Typography className={classes.tooltipDesc}>
                        {message({ id: "connectReferrerTokenToUser" })}
                      </Typography>
                    </span>
                  }
                >
                  <HelpCircleIcon className={classes.tooltipIcon} />
                </Tooltip>
              ),
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <SelectInput
            name="gender"
            items={genderList.map((item: string) => ({
              id: item,
              label: message({ id: item }),
            }))}
            label={message({ id: "gender" })}
            data-test-id="member-gender"
          />
        </Grid>
        <Grid item xs={6}>
          <DateInput
            label={message({ id: "dateOfBirth" })}
            name="birthDate"
            data-test-id="member-birth-date"
            fullWidth
            data-hj-suppress
          />
        </Grid>
        <Grid item xs={6}>
          <DateTimeInput
            label={message({ id: "registrationDate" })}
            name="registeredAt"
            format="yyyy-MM-dd HH:mmXXX"
            data-test-id="member-registration-date"
            width="100%"
          />
        </Grid>
        <Grid item xs={6}>
          <SelectInput
            disabled={!hasAnyTierPermission}
            items={tiersList.map(({ levelId, name }) => ({
              id: levelId,
              label: name,
            }))}
            label={message({ id: "tier" })}
            name="levelId"
            data-test-id="member-tier"
            loadMoreElements={() =>
              !isValidating && setTiersSize(tiersSize + 1)
            }
            anyElementsToFetch={anyTiersToFetch}
            initialValue={
              selectedTier && {
                id: selectedTier.levelId,
                label: selectedTier.name,
              }
            }
          />
        </Grid>
      </Grid>
    </FormSection>
  );
};
